import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { getApiToken, setTokenGetter } from '@proaxiv/server-api'
import { keycloak, getInitPromise } from './keycloak'
import { isPublicEventPath } from './isPublicEventPath'

interface AuthContextValue {
  /** Keycloak subject — stable unique user identifier (`sub` claim). */
  userId: string
  /** Display name derived from the Keycloak token (`preferred_username` or email). */
  username: string
  /** User email from the Keycloak token. */
  email: string
  /** Realm-level roles from `realm_access.roles` in the Keycloak token. */
  realmRoles: ReadonlySet<string>
  /** Organization IDs the user belongs to, from the `org_ids` custom claim. */
  organizationIds: ReadonlyArray<string>
  /** Ends the Keycloak session and redirects to the app origin for re-login. */
  logout: () => void
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextValue | null>(null)

/**
 * Initialises Keycloak, enforces login-required, and provides auth state
 * to the component tree via {@link useAuth}.
 *
 * Public event pages (/e/*) use a silent Keycloak "check-sso" init so that:
 * - Anonymous visitors are never redirected to login (check-sso resolves with
 *   no token and the page renders normally).
 * - Org members who already have an active Keycloak session in the browser get
 *   their token attached to API calls, allowing the backend to serve DRAFT events
 *   with a "draft mode" banner instead of a 404.
 *
 * Must be rendered inside the Redux `<Provider>` so that `setTokenGetter`
 * can wire the access token into RTK Query's `prepareHeaders`.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  // Evaluated once at mount — determines whether this is a public event page.
  const [isPublic] = useState(() => isPublicEventPath(window.location.pathname))
  const [ready, setReady] = useState(false)

  useEffect(() => {
    // Wire the token getter regardless of path. On public pages the token will
    // be present only if the visitor had an active Keycloak session; for
    // anonymous visitors keycloak.token is undefined and prepareHeaders omits
    // the Authorization header.
    setTokenGetter(() => keycloak.token)

    if (isPublic) {
      // Silently check for an existing Keycloak SSO session without redirecting.
      // checkLoginIframe:false avoids the hidden iframe, relying on the session
      // cookie instead — resolves quickly for both logged-in and anonymous users.
      getInitPromise({ onLoad: 'check-sso', checkLoginIframe: false, pkceMethod: 'S256' })
        .then(() => setReady(true))
        .catch(() => setReady(true)) // render the page regardless of SSO check outcome
      return
    }

    let refreshInterval: ReturnType<typeof setInterval>

    // checkLoginIframe:false avoids the hidden cross-origin iframe Keycloak uses to
    // poll session status (auth.* vs app.* origin), which some mobile browsers gate
    // behind a cookie/storage-access consent dialog (see issue #147). Session
    // liveness is already covered by the updateToken()/onTokenExpired logic below.
    getInitPromise({ onLoad: 'login-required', checkLoginIframe: false, pkceMethod: 'S256' })
      .then(() => {
        setReady(true)
        // Proactively refresh before expiry so in-flight requests never use a stale token.
        refreshInterval = setInterval(() => {
          keycloak.updateToken(60).catch(() => {
            keycloak.login()
          })
        }, 30_000)
      })
      .catch(() => {
        // Init failed (Keycloak unreachable) — redirect to login.
        keycloak.login()
      })

    keycloak.onTokenExpired = () => {
      keycloak.updateToken(30).catch(() => {
        keycloak.login()
      })
    }

    return () => {
      clearInterval(refreshInterval)
    }
  }, [isPublic])

  if (!ready) return null

  // On public event pages there is no guaranteed authenticated session, so an anonymous
  // visitor renders children directly without an AuthContext.Provider — none of the public
  // pages call useAuth() themselves, so the missing context value is fine for them.
  // However, a staff member can land on a public event page (e.g. a magic-link ticket URL)
  // while already having an active Keycloak session, and then navigate client-side into a
  // protected route from there (e.g. the ticket page's "Back to event" link) without a full
  // page reload. `isPublic` is frozen for the lifetime of this page load (see
  // isPublicEventPath's docstring), so it must not be the only gate here — otherwise that
  // protected route would render with no AuthContext.Provider at all and every useAuth() call
  // in it would throw. Once a real session exists, always provide the context; only skip it
  // for genuinely anonymous visitors.
  if (isPublic && !keycloak.authenticated) return <>{children}</>

  const tokenParsed = keycloak.tokenParsed as Record<string, unknown> | undefined
  const userId = (tokenParsed?.['sub'] as string | undefined) ?? ''
  const username =
    (tokenParsed?.['preferred_username'] as string | undefined) ??
    (tokenParsed?.['email'] as string | undefined) ??
    ''
  const email = (tokenParsed?.['email'] as string | undefined) ?? ''
  const realmAccess = tokenParsed?.['realm_access'] as { roles?: string[] } | undefined
  const realmRoles: ReadonlySet<string> = new Set(realmAccess?.roles ?? [])
  const organizationIds: ReadonlyArray<string> =
    (tokenParsed?.['org_ids'] as string[] | undefined) ?? []

  const value: AuthContextValue = {
    userId,
    username,
    email,
    realmRoles,
    organizationIds,
    logout: () => {
      void revokeAccessToken().finally(() => {
        keycloak.logout({ redirectUri: window.location.origin })
      })
    },
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

/**
 * Best-effort server-side revocation of the caller's current access token (TC-SEC-002 /
 * CWE-613: Insufficient Session Expiration). Without this, a stateless Keycloak access token
 * remains cryptographically valid -- and usable against the API -- until its natural expiry even
 * after keycloak.logout() has ended the browser session. Any failure here (network error, engine
 * unavailable, etc.) must never block the actual logout, since the Keycloak session termination
 * is the primary mechanism the user is relying on.
 */
async function revokeAccessToken(): Promise<void> {
  const token = getApiToken()
  if (!token) return
  try {
    await fetch('/api/v1/me/logout', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    })
  } catch {
    // Best effort -- the client-side Keycloak session termination below still proceeds.
  }
}

/** Returns the auth context. Must be called inside a component tree wrapped by {@link AuthProvider}. */
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>')
  return ctx
}
